(() => {
  // Numbers only; all text comes from the section's data-recipe-strings JSON (locales/*.json).
  // Each method has five translated steps. `timeline` lines them up with the timer:
  // null = a prep step before you press start, otherwise { t: seconds, to: (recipe) => target grams }.
  const METHOD_CONFIG = {
    v60: {
      ratio: 16.7,
      servingWater: 250,
      doseMin: 10,
      doseMax: 32,
      defaultPeople: 1,
      grind: 'medium_fine',
      time: '2:30-3:00',
      temp: '94-96 °C',
      end: 180,
      steps: ({ coffee, water }) => [
        ['v60_1', { coffee }],
        ['v60_2', { bloom: Math.round(coffee * 3) }],
        ['v60_3', { pour: Math.round(water * 0.6) }],
        ['v60_4', { water }],
        ['v60_5', {}]
      ],
      timeline: [
        null,
        { t: 0, to: ({ coffee }) => coffee * 3 },
        { t: 45, to: ({ water }) => water * 0.6 },
        { t: 90, to: ({ water }) => water },
        { t: 150 }
      ]
    },
    chemex: {
      ratio: 16.7,
      servingWater: 250,
      doseMin: 20,
      doseMax: 60,
      defaultPeople: 3,
      grind: 'medium',
      time: '4:30-5:00',
      temp: '93-96 °C',
      end: 300,
      steps: ({ coffee, water }) => [
        ['chemex_1', { coffee }],
        ['chemex_2', { bloom: Math.round(coffee * 2) }],
        ['chemex_3', { pour: Math.round(water * 0.5) }],
        ['chemex_4', { water }],
        ['chemex_5', {}]
      ],
      timeline: [
        null,
        { t: 0, to: ({ coffee }) => coffee * 2 },
        { t: 45, to: ({ water }) => water * 0.5 },
        { t: 120, to: ({ water }) => water },
        { t: 270 }
      ]
    },
    aeropress: {
      ratio: 13.3,
      servingWater: 200,
      doseMin: 11,
      doseMax: 20,
      defaultPeople: 1,
      grind: 'medium_fine',
      time: '1:30-2:30',
      temp: '90-95 °C',
      end: 120,
      steps: ({ coffee, water }) => [
        ['aeropress_1', { coffee }],
        ['aeropress_2', {}],
        ['aeropress_3', { water }],
        ['aeropress_4', {}],
        ['aeropress_5', {}]
      ],
      timeline: [
        null,
        { t: 0, to: () => 30 },
        { t: 30, to: ({ water }) => water },
        { t: 75 },
        { t: 110 }
      ]
    },
    french_press: {
      ratio: 16.7,
      servingWater: 250,
      doseMin: 15,
      doseMax: 64,
      defaultPeople: 4,
      grind: 'medium_coarse',
      time: '4:00-5:00',
      temp: '94-96 °C',
      end: 300,
      steps: ({ coffee, water }) => [
        ['french_press_1', { coffee }],
        ['french_press_2', { water }],
        ['french_press_3', {}],
        ['french_press_4', {}],
        ['french_press_5', {}]
      ],
      timeline: [null, { t: 0, to: ({ water }) => water }, { t: 20 }, { t: 240 }, { t: 280 }]
    },
    moka_pot: {
      ratio: 10,
      servingWater: 100,
      doseMin: 8,
      doseMax: 30,
      defaultPeople: 1,
      grind: 'medium_fine',
      time: '4:00-6:00',
      tempKey: 'hot_water',
      end: 360,
      untimed: true,
      steps: ({ coffee, water }) => [
        ['moka_pot_1', { water }],
        ['moka_pot_2', { coffee }],
        ['moka_pot_3', {}],
        ['moka_pot_4', {}],
        ['moka_pot_5', {}]
      ]
    },
    batch: {
      ratio: 16.7,
      servingWater: 250,
      doseMin: 30,
      doseMax: 90,
      defaultPeople: 4,
      grind: 'medium',
      time: '5:00-6:30',
      temp: '90-96 °C',
      end: 390,
      untimed: true,
      steps: ({ coffee, water }) => [
        ['batch_1', { coffee }],
        ['batch_2', { water }],
        ['batch_3', {}],
        ['batch_4', {}],
        ['batch_5', {}]
      ]
    },
    espresso: {
      ratio: 2,
      strengthRatios: {
        light: 2.5,
        balanced: 2,
        strong: 1.5
      },
      servingCoffee: 18,
      doseMin: 14,
      doseMax: 22,
      doseStep: 0.5,
      defaultPeople: 1,
      grind: 'fine',
      timeKey: 'espresso_time',
      temp: '90-96 °C',
      end: 30,
      yieldTarget: true,
      steps: ({ coffee, water }) => [
        ['espresso_1', { coffee }],
        ['espresso_2', {}],
        ['espresso_3', { water }],
        ['espresso_4', {}],
        ['espresso_5', {}]
      ],
      timeline: [null, null, { t: 0, to: ({ water }) => water }, { t: 25 }, { t: 30 }]
    }
  };

  const STRENGTH_OFFSET = { light: 1, balanced: 0, strong: -1 };
  const RING_LENGTH = 339.3;

  // Replaces {name} placeholders in a translated template.
  const fill = (template, values = {}) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, name) => (name in values ? values[name] : match));

  const formatGrams = (value) => {
    const rounded = Math.round(value * 2) / 2;
    return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  };

  const clock = (seconds) => {
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        // Fall back below for browser contexts that block the async Clipboard API.
      }
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.insetBlockStart = '-100vh';
    document.body.append(textArea);
    textArea.select();

    try {
      return document.execCommand('copy');
    } finally {
      textArea.remove();
    }
  };

  class CoffeeRecipeGenerator extends HTMLElement {
    connectedCallback() {
      this.strings = JSON.parse(this.querySelector('[data-recipe-strings]')?.textContent || '{}');
      this.els = {
        output: this.querySelector('[data-recipe-output]'),
        title: this.querySelector('[data-recipe-title]'),
        steps: this.querySelector('[data-recipe-steps]'),
        dose: this.querySelector('[data-dose]'),
        doseLabel: this.querySelector('[data-dose-label]'),
        serves: this.querySelector('[data-serves]'),
        waterLabel: this.querySelector('[data-spec-label="water"]'),
        ring: this.querySelector('[data-ring]'),
        clock: this.querySelector('[data-clock]'),
        target: this.querySelector('[data-target]'),
        next: this.querySelector('[data-next]'),
        start: this.querySelector('[data-start]'),
        reset: this.querySelector('[data-reset]'),
        note: this.querySelector('[data-timer-note]'),
        copy: this.querySelector('[data-copy-recipe]'),
        announce: this.querySelector('[data-announce]')
      };
      if (!this.els.output || !this.els.dose) return;

      this.methodKey = 'v60';
      this.strengthKey = 'balanced';
      this.elapsed = 0;
      this.startedAt = 0;
      this.running = false;
      this.lastStep = -1;
      this.wakeLock = null;

      this.querySelectorAll('[data-method]').forEach((button) =>
        button.addEventListener('click', () => this.selectMethod(button.dataset.method))
      );
      this.querySelectorAll('[data-strength]').forEach((button) =>
        button.addEventListener('click', () => this.selectStrength(button.dataset.strength))
      );
      this.els.dose.addEventListener('input', () => {
        this.dose = Number(this.els.dose.value);
        this.render();
      });
      this.els.start?.addEventListener('click', () => (this.running ? this.pause() : this.start()));
      this.els.reset?.addEventListener('click', () => this.reset());
      this.els.copy?.addEventListener('click', () => this.copyRecipe());
      this.onVisibility = () => {
        if (document.visibilityState === 'visible' && this.running) this.requestWakeLock();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.selectMethod(this.methodKey);
    }

    disconnectedCallback() {
      this.pause();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    get method() {
      return METHOD_CONFIG[this.methodKey] || METHOD_CONFIG.v60;
    }

    ratio() {
      const method = this.method;
      return method.strengthRatios?.[this.strengthKey] ?? Math.max(1.8, method.ratio + STRENGTH_OFFSET[this.strengthKey]);
    }

    defaultDose(method) {
      if (method.servingCoffee) return method.servingCoffee * method.defaultPeople;
      return Math.round((method.servingWater * method.defaultPeople) / method.ratio);
    }

    selectMethod(key) {
      this.methodKey = METHOD_CONFIG[key] ? key : 'v60';
      const method = this.method;
      this.querySelectorAll('[data-method]').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.method === this.methodKey))
      );

      this.dose = this.defaultDose(method);
      Object.assign(this.els.dose, { min: method.doseMin, max: method.doseMax, step: method.doseStep || 1 });
      this.els.dose.value = this.dose;
      this.els.note.hidden = !method.untimed;
      this.reset();
    }

    selectStrength(key) {
      this.strengthKey = key in STRENGTH_OFFSET ? key : 'balanced';
      this.querySelectorAll('[data-strength]').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.strength === this.strengthKey))
      );
      this.render();
    }

    getRecipe() {
      const s = this.strings;
      const method = this.method;
      const ratio = this.ratio();
      const coffee = this.dose;
      // Round brew water to 5 g so pour targets land on numbers you can read off a scale.
      const water = method.servingCoffee ? coffee * ratio : Math.round((coffee * ratio) / 5) * 5;
      const people = Math.max(
        1,
        Math.round(method.servingCoffee ? coffee / method.servingCoffee : water / method.servingWater)
      );

      return {
        method,
        methodLabel: s.methods?.[this.methodKey] || this.methodKey,
        strengthLabel: s.strength?.[this.strengthKey] || this.strengthKey,
        grindLabel: s.grind_sizes?.[method.grind] || method.grind,
        timeLabel: method.timeKey ? s[method.timeKey] : method.time,
        tempLabel: method.tempKey ? s[method.tempKey] : method.temp,
        people,
        coffee,
        water,
        coffeeLabel: formatGrams(coffee),
        waterLabel: String(Math.round(water)),
        ratioLabel: `1:${ratio.toFixed(1)}`
      };
    }

    peopleLabel(count) {
      return fill(count === 1 ? this.strings.people_one : this.strings.people_other, { count });
    }

    /** Translated steps merged with their place on the timer. */
    getSteps(recipe) {
      const values = { ...recipe, coffee: recipe.coffeeLabel, water: recipe.waterLabel };
      return recipe.method.steps(values).map(([key, placeholders], index) => {
        const timing = recipe.method.timeline?.[index] ?? null;
        return {
          text: fill(this.strings.steps?.[key], placeholders),
          t: timing ? timing.t : null,
          to: timing?.to ? Math.round(timing.to(recipe)) : null
        };
      });
    }

    targetLabel(grams) {
      if (grams == null) return '';
      if (this.method.yieldTarget) return `${this.strings.yield}: ${grams} g`;
      return fill(this.strings.pour_to, { grams });
    }

    render() {
      const s = this.strings;
      const recipe = this.getRecipe();
      this.recipe = recipe;
      this.stepList = this.getSteps(recipe);
      this.timedSteps = this.stepList.filter((step) => step.t != null);

      this.els.title.textContent = fill(s.title, { method: recipe.methodLabel, people: this.peopleLabel(recipe.people) });
      this.els.doseLabel.textContent = `${recipe.coffeeLabel} g`;
      this.els.serves.textContent = fill(s.serves, { count: recipe.people });
      this.els.waterLabel.textContent = recipe.method.yieldTarget ? s.yield : s.water;

      const specs = {
        coffee: `${recipe.coffeeLabel} g`,
        water: `${recipe.waterLabel} g`,
        ratio: recipe.ratioLabel,
        temp: recipe.tempLabel,
        grind: recipe.grindLabel
      };
      Object.entries(specs).forEach(([key, value]) => {
        const el = this.querySelector(`[data-spec="${key}"]`);
        if (el) el.textContent = value;
      });

      const dose = this.els.dose;
      dose.style.setProperty('--fill', `${((dose.value - dose.min) / (dose.max - dose.min)) * 100}%`);

      this.els.steps.replaceChildren(
        ...this.stepList.map((step) => {
          const li = document.createElement('li');
          li.classList.toggle('is-prep', step.t == null);
          const time = document.createElement('span');
          time.className = 'coffee-recipe-generator__step-time';
          time.textContent = step.t == null ? s.prep : clock(step.t);
          const text = document.createElement('span');
          text.textContent = step.text;
          const to = document.createElement('span');
          to.className = 'coffee-recipe-generator__step-target';
          to.textContent = step.to == null ? '' : `${step.to} g`;
          li.append(time, text, to);
          return li;
        })
      );

      this.lastStep = -1;
      this.tick(true);
    }

    currentIndex(t) {
      let index = -1;
      this.timedSteps.forEach((step, i) => {
        if (t >= step.t) index = i;
      });
      return index;
    }

    tick(force = false) {
      const s = this.strings;
      const t = this.running ? this.elapsed + (performance.now() - this.startedAt) / 1000 : this.elapsed;
      const end = this.method.end;
      if (this.running && t >= end) {
        this.finish();
        return;
      }
      this.els.clock.textContent = clock(t);
      this.els.ring.style.strokeDashoffset = String(RING_LENGTH * (1 - Math.min(1, t / end)));

      const started = this.running || t > 0;
      const index = started ? this.currentIndex(t) : -1;
      const current = this.timedSteps[index];

      [...this.els.steps.children].forEach((li, i) => {
        const stepIndex = this.timedSteps.indexOf(this.stepList[i]);
        li.classList.toggle('is-now', stepIndex !== -1 && stepIndex === index);
        li.classList.toggle('is-done', started && (stepIndex === -1 ? index >= 0 : stepIndex < index));
      });

      if (!started) {
        this.els.target.textContent = s.ready;
        this.els.next.textContent = this.method.untimed ? '' : s.press_start;
      } else if (this.finished) {
        this.els.target.textContent = s.done;
        this.els.next.textContent = '';
      } else if (this.method.untimed) {
        this.els.target.textContent = '';
        this.els.next.textContent = '';
      } else if (current) {
        const upcoming = this.timedSteps[index + 1];
        this.els.target.textContent =
          this.targetLabel(current.to) || fill(s.step_of, { current: index + 1, total: this.timedSteps.length });
        this.els.next.textContent = upcoming ? fill(s.next_in, { time: clock(upcoming.t - t) }) : '';
      }

      if (index !== this.lastStep) {
        if (this.running && !force && current) {
          this.els.announce.textContent = `${clock(current.t)}. ${this.targetLabel(current.to)} ${current.text}`;
          navigator.vibrate?.(120);
        }
        this.lastStep = index;
      }

      if (this.running) this.frame = requestAnimationFrame(() => this.tick());
    }

    async requestWakeLock() {
      try {
        this.wakeLock = await navigator.wakeLock?.request('screen');
      } catch (error) {
        this.wakeLock = null;
      }
    }

    start() {
      if (this.finished) this.reset();
      this.running = true;
      this.startedAt = performance.now();
      this.els.start.textContent = this.strings.pause;
      this.els.output.classList.add('is-brewing');
      this.requestWakeLock();
      this.tick();
    }

    pause() {
      if (!this.running) return;
      this.elapsed += (performance.now() - this.startedAt) / 1000;
      this.running = false;
      cancelAnimationFrame(this.frame);
      this.els.start.textContent = this.strings.resume;
      this.wakeLock?.release?.();
      this.wakeLock = null;
    }

    /** Stops the timer at the end of the brew with the ring full. */
    finish() {
      this.pause();
      this.elapsed = this.method.end;
      this.finished = true;
      this.els.start.textContent = this.strings.start;
      this.els.announce.textContent = this.strings.done;
      navigator.vibrate?.([120, 80, 120]);
      this.tick(true);
    }

    reset() {
      this.pause();
      this.elapsed = 0;
      this.finished = false;
      this.els.start.textContent = this.strings.start;
      this.els.output.classList.remove('is-brewing');
      this.render();
    }

    async copyRecipe() {
      const s = this.strings;
      const recipe = this.recipe || this.getRecipe();
      const steps = this.getSteps(recipe)
        .map((step, index) => `${index + 1}. ${step.t == null ? '' : `${clock(step.t)} `}${step.text}`)
        .join('\n');
      const text = `${fill(s.copy_title, { method: recipe.methodLabel })}
${s.people}: ${recipe.people}
${s.cup_style}: ${recipe.strengthLabel}
${s.coffee}: ${recipe.coffeeLabel} g
${recipe.method.yieldTarget ? s.yield : s.water}: ${recipe.waterLabel} g
${s.ratio}: ${recipe.ratioLabel}
${s.grind}: ${recipe.grindLabel}
${s.brew_time}: ${recipe.timeLabel}

${steps}`;

      if (await copyText(text)) {
        this.els.copy.textContent = s.copied;
        window.setTimeout(() => {
          this.els.copy.textContent = s.copy_recipe;
        }, 1400);
      } else {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(this.els.output);
        selection.removeAllRanges();
        selection.addRange(range);
        this.els.copy.textContent = s.recipe_selected;
      }
    }
  }

  if (!customElements.get('coffee-recipe-generator')) {
    customElements.define('coffee-recipe-generator', CoffeeRecipeGenerator);
  }
})();
